import React from 'react';
import { Icon } from '@/components/ui/Icon';
import { Avatar } from '@/components/console/Avatar';
import { ChannelDot } from '@/components/console/ChannelDot';
import { TagChip, type TagChipTag } from './TagChip';
import { useT } from '@/i18n/labels';
import { useAuthStore } from '@/stores/authStore';
import { useAgentStore } from '@/stores/agentStore';
import { useConversationStore } from '@/stores/conversationStore';
import { getConversationLabel, getConversationPreview, getCustomerDisplayName } from '@/utils/conversationLabel';
import { formatRelative } from '@/utils/formatters';
import { formatDraftAge, isDraftHeld } from '@/utils/draftReview';
import { customerAskedForPerson } from '@/utils/consultationReason';
import type { Conversation } from '@/types';

/** How many tag chips a row shows before it says "+N more". */
export const ROW_MAX_TAGS = 3;

interface ConvRowProps {
  c: Conversation;
  active: boolean;
  onClick: () => void;
  /**
   * Do not draw the "Team shared" chip: the list is a folder in which EVERY row is team shared by definition (Needs
   * consultation, Bot handle, Customer waiting, All shared), so the chip would say the same thing on every row.
   */
  hideTeamChip?: boolean;
}

/** `formatRelative` throws on a time it cannot parse; a row must never take the list down with it. */
function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return formatRelative(iso);
  } catch {
    return '';
  }
}

/** A single conversation row of the folder list. */
export const ConvRow: React.FC<ConvRowProps> = ({ c, active, onClick, hideTeamChip = false }) => {
  const { t, lang, urgencyLabel } = useT();
  const me = useAuthStore((s) => s.user?.agentId);
  const directory = useAgentStore((s) => s.directory);
  const tagCatalog = useConversationStore((s) => s.tagCatalog);

  const label = getConversationLabel(c);
  const preview = getConversationPreview(c);
  const identified = !!(c.customerDisplayName && c.customerDisplayName.trim());
  // The customer line: the person's name when the row's title is something else (an email subject); empty when the
  // title already IS the name, so the name is never printed twice.
  const customerName = getCustomerDisplayName(c);
  const who = customerName && customerName !== label ? customerName : '';
  const showUrgency = c.slaUrgency !== 'NORMAL' && c.state !== 'CLOSED';
  const askedForPerson = customerAskedForPerson(c);

  // OP-582 — a draft awaiting review had NO presence in the list at all: no badge, no count, no filter. The card rendered only
  // inside the thread of the SELECTED conversation, so the only way to learn a customer was waiting was to already know. This
  // chip is the row-level half of the fix; the Drafts folder and the rail badge are the other two.
  const draft = c.pendingDraft;
  const draftAge = draft ? formatDraftAge(draft.createdAtUtc, lang) : null;
  const draftLabel = draft
    ? t(isDraftHeld(draft) ? 'draft_badge_held' : 'draft_badge_waiting', { time: draftAge ?? '' })
    : null;

  // WHO owns it, as the server said (`ownerAgentId`; never `assignedAgentId`): a staff id = owned, null = nobody (team
  // shared), undefined / blank = not known, which draws NO chip (an unknown owner is not "team shared"). The console
  // never decides ownership - it names what the row carries. The lock and the "Private to" title promise PRIVACY, so
  // they appear only while the server says private ownership is in effect on this row (`ownershipEnforced`).
  const ownerId = typeof c.ownerAgentId === 'string' && c.ownerAgentId.trim() ? c.ownerAgentId.trim() : undefined;
  const nobodyOwns = c.ownerAgentId === null;
  const enforced = c.ownershipEnforced === true;
  const mine = !!ownerId && !!me && ownerId === me;
  const otherOwnerName = ownerId && !mine ? directory[ownerId] || t('agentFallbackName') : null;

  const tagKeys = Array.isArray(c.tags) ? c.tags : [];
  const chipTags: TagChipTag[] = tagKeys.map((key) => {
    const def = tagCatalog.find((x) => x.key === key);
    // A key the catalog does not know (a retired tag, or the catalog has not loaded) stays visible as its raw key, muted.
    return def ? def : { key, label: key, unknown: true };
  });
  const shownTags = chipTags.slice(0, ROW_MAX_TAGS);
  const moreTags = chipTags.length - shownTags.length;

  const time = relativeTime(c.lastActivityAt || c.lastMessageTimestamp);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={'ac-conv' + (active ? ' active' : '')}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      aria-current={active ? 'true' : undefined}
      data-testid={`conv-row-${c.sessionId}`}
      data-active={active}
    >
      <Avatar name={label} seed={c.sessionId} size={34} className="ac-conv-av">
        <ChannelDot channel={c.channel} />
      </Avatar>
      {/* Three lines, like the help desks agents already use (operator 2026-10-12, HubSpot for reference): what it is and
          when, who it is with (plus owner / held draft / urgency, small and inline), and one line of the last message.
          Tags get a fourth line only when there are any. The state is the folder's job, not a pill on every row. */}
      <div className="ac-conv-body">
        <div className="ac-conv-top">
          <span className="ac-conv-name">{label}</span>
          {c.unreadCount > 0 && (
            <span className="ac-unread" aria-label={String(c.unreadCount)}>
              {c.unreadCount}
            </span>
          )}
          <span className="ac-conv-time" data-testid={`conv-time-${c.sessionId}`}>
            {time}
          </span>
        </div>
        <div className="ac-conv-who">
          {who ? (
            <span className="ac-conv-customer" dir="auto">
              {who}
            </span>
          ) : (
            !identified && <span className="ac-conv-customer muted">{t('unidentified')}</span>
          )}
          {mine && (
            <span
              className="ac-conv-owner mine"
              data-testid={`conv-owner-${c.sessionId}`}
              data-owner="me"
              title={t('team_you')}
            >
              <Icon name="user" size={10} />
              {t('team_you')}
            </span>
          )}
          {otherOwnerName && (
            <span
              className="ac-conv-owner other"
              data-testid={`conv-owner-${c.sessionId}`}
              data-owner="other"
              title={enforced ? t('row_private_to', { name: otherOwnerName }) : otherOwnerName}
            >
              {enforced && (
                <span className="cl-lock" data-testid={`conv-lock-${c.sessionId}`}>
                  <Icon name="lock" size={10} />
                </span>
              )}
              <span dir="auto">{otherOwnerName}</span>
            </span>
          )}
          {!ownerId && nobodyOwns && !hideTeamChip && (
            <span
              className="ac-conv-owner team"
              data-testid={`conv-owner-${c.sessionId}`}
              data-owner="team"
              title={t('cp_owner_team')}
            >
              <Icon name="users" size={10} />
              {t('cp_owner_team')}
            </span>
          )}
          {draft && (
            <span
              className="ac-draft-chip"
              data-testid={`conv-draft-badge-${c.sessionId}`}
              data-held={isDraftHeld(draft) ? 'true' : 'false'}
              title={draftLabel ?? undefined}
            >
              <Icon name="mail" size={11} />
              {draftLabel}
            </span>
          )}
          {/* 2026-10-28 (operator) - the bot is waiting for the team because the CUSTOMER asked for a person. */}
          {askedForPerson && (
            <span className="ac-human-chip" data-testid={`conv-human-badge-${c.sessionId}`} title={t('human_banner')}>
              <Icon name="hand" size={11} />
              {t('human_badge')}
            </span>
          )}
          {showUrgency && (
            <span className={'ac-conv-urgency' + (c.slaUrgency === 'CRITICAL' ? ' critical' : '')}>
              {urgencyLabel(c.slaUrgency)}
            </span>
          )}
        </div>
        {preview && <div className="ac-conv-prev">{preview}</div>}
        {shownTags.length > 0 && (
          <div className="ac-conv-chips">
            {shownTags.map((g) => (
              <TagChip key={g.key} tag={g} small />
            ))}
            {moreTags > 0 && (
              <span className="tag-more" data-testid={`conv-tags-more-${c.sessionId}`}>
                {t('row_tags_more', { n: moreTags })}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
